import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Plus, KeyRound, ShieldCheck, ShieldOff, X, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const ROLE_OPTIONS = [
  { value: "admin", label: "Administrator" },
  { value: "employee", label: "Mitarbeiter" },
];

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString("de-DE")} ${d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`;
}

export default function Benutzer() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showReset, setShowReset] = useState(null); // user object
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [createForm, setCreateForm] = useState({ email: "", name: "", role: "employee", password: "" });

  async function refresh() {
    setLoading(true);
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Benutzer konnten nicht geladen werden");
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  async function createUser(e) {
    e.preventDefault();
    if (createForm.password.length < 12) {
      toast.error("Passwort muss mindestens 12 Zeichen lang sein");
      return;
    }
    try {
      await api.post("/users", createForm);
      toast.success("Benutzer angelegt");
      setShowCreate(false);
      setCreateForm({ email: "", name: "", role: "employee", password: "" });
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Anlegen fehlgeschlagen");
    }
  }

  async function toggleActive(u) {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Benutzer deaktiviert" : "Benutzer reaktiviert");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Statusänderung fehlgeschlagen");
    }
  }

  async function changeRole(u, newRole) {
    if (newRole === u.role) return;
    try {
      await api.patch(`/users/${u.id}`, { role: newRole });
      toast.success("Rolle geändert");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Rollenwechsel fehlgeschlagen");
    }
  }

  async function deleteUser(u) {
    if (me?.id === u.id) { toast.error("Eigenen Zugang kann man nicht löschen"); return; }
    const msg = `„${u.email}" wirklich löschen?\n\nFalls Buchungen/POS-Belege existieren wird der Datensatz archiviert (Audit bleibt erhalten). Sonst endgültig entfernt.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api.delete(`/users/${u.id}`);
      toast.success(r.data?.archived ? "Benutzer archiviert (Audit bleibt)" : "Benutzer endgültig gelöscht");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Löschen fehlgeschlagen");
    }
  }

  async function submitReset(e) {
    e.preventDefault();
    if (pw1.length < 12) { toast.error("Passwort muss mindestens 12 Zeichen lang sein"); return; }
    if (pw1 !== pw2) { toast.error("Passwörter stimmen nicht überein"); return; }
    try {
      await api.post(`/users/${showReset.id}/password`, { new_password: pw1 });
      toast.success(`Passwort für ${showReset.email} zurückgesetzt`);
      setShowReset(null); setPw1(""); setPw2("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset fehlgeschlagen");
    }
  }

  return (
    <div data-testid="benutzer-page">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Users className="text-cyan-400 shrink-0" size={28} />
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight truncate">Benutzer</h1>
        </div>
        <Button data-testid="user-create-btn" onClick={() => setShowCreate(true)} className="rounded-sm w-full sm:w-auto">
          <Plus size={18} className="mr-2" /> Neuer Benutzer
        </Button>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2" data-testid="user-mobile-list">
        {loading ? (
          <div className="p-8 text-center text-zinc-500 text-sm">Lade…</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">Keine Benutzer vorhanden</div>
        ) : users.map((u) => {
          const isSelf = me?.id === u.id;
          return (
            <div key={u.id} data-testid={`user-row-m-${u.id}`} className="border border-zinc-800 rounded-lg bg-zinc-950 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{u.name}</div>
                  <div className="text-xs text-zinc-500 font-mono break-anywhere">{u.email}</div>
                </div>
                <span
                  className={`shrink-0 inline-block px-2 py-0.5 rounded-sm text-xs font-mono uppercase ${u.is_active ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-800 text-zinc-500"}`}
                >
                  {u.is_active ? "aktiv" : "deaktiviert"}
                </span>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <select
                  data-testid={`user-role-m-${u.id}`}
                  value={u.role}
                  disabled={isSelf}
                  onChange={(e) => changeRole(u, e.target.value)}
                  className="bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1.5 text-sm disabled:opacity-50 flex-1 min-w-0"
                >
                  {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  {u.role === "customer" && <option value="customer">Kunde</option>}
                </select>
                {isSelf && <span className="text-xs text-cyan-400 shrink-0">(du)</span>}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  data-testid={`user-reset-btn-m-${u.id}`}
                  size="sm"
                  variant="outline"
                  className="border-zinc-700 flex-1 min-w-[8rem]"
                  onClick={() => { setShowReset(u); setPw1(""); setPw2(""); }}
                >
                  <KeyRound size={14} className="mr-1" /> Passwort
                </Button>
                <Button
                  data-testid={`user-toggle-btn-m-${u.id}`}
                  size="sm"
                  variant="outline"
                  disabled={isSelf}
                  className={`border-zinc-700 flex-1 min-w-[8rem] ${u.is_active ? "text-rose-300" : "text-emerald-300"}`}
                  onClick={() => toggleActive(u)}
                >
                  {u.is_active ? <><ShieldOff size={14} className="mr-1" />Deaktivieren</> : <><ShieldCheck size={14} className="mr-1" />Reaktivieren</>}
                </Button>
                <Button
                  data-testid={`user-delete-btn-m-${u.id}`}
                  size="sm" variant="outline" disabled={isSelf}
                  className="border-rose-800 text-rose-300 flex-1 min-w-[6rem]"
                  onClick={() => deleteUser(u)}
                >
                  <Trash2 size={14} className="mr-1" /> Löschen
                </Button>
              </div>
              <div className="mt-2 text-xs text-zinc-500 font-mono">{formatDate(u.created_at)}</div>
            </div>
          );
        })}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block border border-zinc-800 rounded-lg bg-zinc-950 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-zinc-400 uppercase tracking-wider text-xs">
            <tr>
              <th className="text-left px-4 py-3">Name / E-Mail</th>
              <th className="text-left px-4 py-3">Rolle</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Angelegt</th>
              <th className="text-right px-4 py-3">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center text-zinc-500 py-10">Lade…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={5} className="text-center text-zinc-500 py-10">Keine Benutzer vorhanden</td></tr>
            ) : users.map((u) => {
              const isSelf = me?.id === u.id;
              return (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-zinc-900">
                  <td className="px-4 py-3">
                    <div className="font-semibold">{u.name}</div>
                    <div className="text-xs text-zinc-500 font-mono">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      data-testid={`user-role-${u.id}`}
                      value={u.role}
                      disabled={isSelf}
                      onChange={(e) => changeRole(u, e.target.value)}
                      className="bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1 text-sm disabled:opacity-50"
                    >
                      {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                      {u.role === "customer" && <option value="customer">Kunde</option>}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      data-testid={`user-status-${u.id}`}
                      className={`inline-block px-2 py-0.5 rounded-sm text-xs font-mono uppercase ${u.is_active ? "bg-emerald-500/20 text-emerald-300" : "bg-zinc-800 text-zinc-500"}`}
                    >
                      {u.is_active ? "aktiv" : "deaktiviert"}
                    </span>
                    {isSelf && <span className="ml-2 text-xs text-cyan-400">(du)</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500 font-mono">{formatDate(u.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-wrap gap-2 justify-end">
                      <Button
                        data-testid={`user-reset-btn-${u.id}`}
                        size="sm"
                        variant="outline"
                        className="border-zinc-700"
                        onClick={() => { setShowReset(u); setPw1(""); setPw2(""); }}
                      >
                        <KeyRound size={14} className="mr-1" /> Passwort
                      </Button>
                      <Button
                        data-testid={`user-toggle-btn-${u.id}`}
                        size="sm"
                        variant="outline"
                        disabled={isSelf}
                        className={`border-zinc-700 ${u.is_active ? "text-rose-300" : "text-emerald-300"}`}
                        onClick={() => toggleActive(u)}
                      >
                        {u.is_active ? <><ShieldOff size={14} className="mr-1" />Deaktivieren</> : <><ShieldCheck size={14} className="mr-1" />Reaktivieren</>}
                      </Button>
                      <Button
                        data-testid={`user-delete-btn-${u.id}`}
                        size="sm" variant="outline" disabled={isSelf}
                        className="border-rose-800 text-rose-300"
                        onClick={() => deleteUser(u)}
                      >
                        <Trash2 size={14} className="mr-1" /> Löschen
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-4" data-testid="user-create-modal">
          <form onSubmit={createUser} className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <div className="font-display text-lg font-bold uppercase tracking-wide">Neuer Benutzer</div>
              <Button type="button" size="icon" variant="ghost" onClick={() => setShowCreate(false)}><X size={18} /></Button>
            </div>
            <div>
              <Label>Name</Label>
              <Input required data-testid="user-create-name" value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} />
            </div>
            <div>
              <Label>E-Mail</Label>
              <Input required type="email" data-testid="user-create-email" value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} />
            </div>
            <div>
              <Label>Rolle</Label>
              <select
                data-testid="user-create-role"
                value={createForm.role}
                onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm"
              >
                {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Passwort <span className="text-xs text-zinc-500">(min. 12 Zeichen)</span></Label>
              <Input required type="password" minLength={12} data-testid="user-create-password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} />
            </div>
            <Button type="submit" data-testid="user-create-submit" className="w-full">Anlegen</Button>
          </form>
        </div>
      )}

      {/* Reset password modal */}
      {showReset && (
        <div className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-4" data-testid="user-reset-modal">
          <form onSubmit={submitReset} className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <div className="font-display text-lg font-bold uppercase tracking-wide">Passwort zurücksetzen</div>
              <Button type="button" size="icon" variant="ghost" onClick={() => setShowReset(null)}><X size={18} /></Button>
            </div>
            <div className="text-sm text-zinc-400">Für <span className="font-mono text-zinc-200">{showReset.email}</span>. Alle aktiven Sitzungen des Benutzers werden abgemeldet.</div>
            <div>
              <Label>Neues Passwort <span className="text-xs text-zinc-500">(min. 12 Zeichen)</span></Label>
              <Input required type="password" minLength={12} data-testid="user-reset-pw1" value={pw1} onChange={(e) => setPw1(e.target.value)} />
            </div>
            <div>
              <Label>Wiederholen</Label>
              <Input required type="password" minLength={12} data-testid="user-reset-pw2" value={pw2} onChange={(e) => setPw2(e.target.value)} />
            </div>
            <Button type="submit" data-testid="user-reset-submit" className="w-full">Zurücksetzen</Button>
          </form>
        </div>
      )}
    </div>
  );
}
